/**
 * Cycle detection, layer assignment and hop distances for the shareholder graph.
 *
 * Edges are owner -> owned: `{ id, source: ownerId, target: ownedId, isSubsidiary? }`.
 * Everything is iterative (no recursion), so a 15-level ownership chain or a 300-node graph can
 * never overflow the stack, and every adjacency list is sorted by id, so the result does not
 * depend on the order the BE sent the rows in.
 */

// Code-unit order (not locale order): deterministic for the numeric registry ids.
const compareStrings = (a, b) => {
  if (a < b) return -1;
  return a > b ? 1 : 0;
};

const byId = (a, b) => compareStrings(a.id, b.id);

const pushToMapList = (map, key, value) => {
  const list = map.get(key);
  if (list) {
    list.push(value);
  } else {
    map.set(key, [value]);
  }
};

/**
 * owned -> [{ id: ownerId, edgeId }]: the direction the DFS walks (from the target up to its
 * owners), so every ownership edge that points down towards the target stays a forward edge.
 */
const buildUpwardAdjacency = (edges) => {
  const adjacency = new Map();
  edges.forEach((edge) =>
    pushToMapList(adjacency, edge.target, { id: edge.source, edgeId: edge.id })
  );
  adjacency.forEach((list) => list.sort(byId));
  return adjacency;
};

const createTarjanState = () => ({
  index: 0,
  indexOf: new Map(),
  lowLink: new Map(),
  sccStack: [],
  onSccStack: new Set(),
  onPath: new Set(),
  backEdgeIds: new Set(),
  cycleNodeIds: new Set()
});

const openNode = (state, nodeId) => {
  state.indexOf.set(nodeId, state.index);
  state.lowLink.set(nodeId, state.index);
  state.index += 1;
  state.sccStack.push(nodeId);
  state.onSccStack.add(nodeId);
  state.onPath.add(nodeId);
};

const popComponent = (state, rootId) => {
  const component = [];
  let member;
  do {
    member = state.sccStack.pop();
    state.onSccStack.delete(member);
    component.push(member);
  } while (member !== rootId);
  if (component.length > 1) {
    component.forEach((id) => state.cycleNodeIds.add(id));
  }
};

const closeNode = (state, frames) => {
  const { nodeId } = frames.pop();
  state.onPath.delete(nodeId);
  if (state.lowLink.get(nodeId) === state.indexOf.get(nodeId)) {
    popComponent(state, nodeId);
  }
  const parent = frames.at(-1);
  if (parent) {
    state.lowLink.set(
      parent.nodeId,
      Math.min(state.lowLink.get(parent.nodeId), state.lowLink.get(nodeId))
    );
  }
};

// Handles one neighbour of the frame on top of the stack; returns a new frame to descend into.
const visitNeighbour = (state, frame, neighbour) => {
  if (!state.indexOf.has(neighbour.id)) {
    openNode(state, neighbour.id);
    return { nodeId: neighbour.id, next: 0 };
  }
  if (state.onPath.has(neighbour.id)) {
    state.backEdgeIds.add(neighbour.edgeId);
  }
  if (state.onSccStack.has(neighbour.id)) {
    state.lowLink.set(
      frame.nodeId,
      Math.min(state.lowLink.get(frame.nodeId), state.indexOf.get(neighbour.id))
    );
  }
  return null;
};

const runDfsFrom = (state, adjacency, startId) => {
  openNode(state, startId);
  const frames = [{ nodeId: startId, next: 0 }];
  while (frames.length) {
    const frame = frames.at(-1);
    const neighbours = adjacency.get(frame.nodeId) || [];
    if (frame.next >= neighbours.length) {
      closeNode(state, frames);
    } else {
      const neighbour = neighbours[frame.next];
      frame.next += 1;
      const child = visitNeighbour(state, frame, neighbour);
      if (child) frames.push(child);
    }
  }
};

/**
 * Iterative Tarjan SCC plus DFS back-edge detection.
 *
 * The DFS starts at rootId and walks owned -> owner; remaining nodes are started in id order.
 * An edge to a node that is still on the DFS path closes a cycle and is a back edge; removing
 * all back edges leaves a DAG. For a company that is both owner and subsidiary of the target,
 * the target -> company edge is the back edge, so the company is laid out as an owner.
 *
 * @param {string[]} nodeIds
 * @param {Array<{id: string, source: string, target: string}>} edges - owner -> owned
 * @param {string} rootId - the target company
 * @returns {{backEdgeIds: Set<string>, cycleNodeIds: Set<string>}} cycleNodeIds = members of
 *   strongly connected components with more than one node
 */
export const findBackEdges = (nodeIds, edges, rootId) => {
  const adjacency = buildUpwardAdjacency(edges);
  const state = createTarjanState();
  const known = new Set(nodeIds);
  const starts = [rootId, ...[...known].filter((id) => id !== rootId).sort(compareStrings)];
  starts.forEach((id) => {
    if (known.has(id) && !state.indexOf.has(id)) {
      runDfsFrom(state, adjacency, id);
    }
  });
  return { backEdgeIds: state.backEdgeIds, cycleNodeIds: state.cycleNodeIds };
};

const layerEdges = (edges, backEdgeIds, subsidiaryIds) =>
  edges.filter(
    (edge) =>
      !backEdgeIds.has(edge.id) &&
      !subsidiaryIds.has(edge.target) &&
      !subsidiaryIds.has(edge.source)
  );

/**
 * Longest-path levels: target 0, owners negative, subsidiaries +1.
 *
 * Owners take the deepest level over all their holdings (Kahn order on the acyclic remainder),
 * so every layout edge points down and an owner holding both directly and through a holding sits
 * above the holding. Nodes without any path to the target (not expected after pruning) get -1.
 *
 * @param {string[]} nodeIds
 * @param {Array<{id, source, target}>} edges - owner -> owned
 * @param {string} targetId
 * @param {{backEdgeIds?: Set<string>, subsidiaryIds?: Set<string>}} [options]
 * @returns {Map<string, number>}
 */
export const assignLevels = (nodeIds, edges, targetId, options = {}) => {
  const backEdgeIds = options.backEdgeIds || new Set();
  const subsidiaryIds = options.subsidiaryIds || new Set();
  const usable = layerEdges(edges, backEdgeIds, subsidiaryIds);
  const remainingHoldings = new Map();
  const ownersOf = new Map();
  usable.forEach((edge) => {
    remainingHoldings.set(edge.source, (remainingHoldings.get(edge.source) || 0) + 1);
    pushToMapList(ownersOf, edge.target, edge.source);
  });

  const levels = new Map([[targetId, 0]]);
  const queue = [targetId];
  while (queue.length) {
    const ownedId = queue.shift();
    const ownedLevel = levels.get(ownedId);
    [...(ownersOf.get(ownedId) || [])].sort(compareStrings).forEach((ownerId) => {
      if (ownerId === targetId) return;
      levels.set(ownerId, Math.min(levels.get(ownerId) ?? Infinity, ownedLevel - 1));
      remainingHoldings.set(ownerId, remainingHoldings.get(ownerId) - 1);
      if (remainingHoldings.get(ownerId) === 0) queue.push(ownerId);
    });
  }

  nodeIds.forEach((id) => {
    if (subsidiaryIds.has(id)) {
      levels.set(id, 1);
    } else if (!levels.has(id)) {
      levels.set(id, -1);
    }
  });
  return levels;
};

/**
 * Shortest number of hops to the target, for the mobile grouping ('Level 1 · direct
 * shareholders'). BFS up the ownership edges; direct subsidiaries are 1.
 *
 * Back edges count here: a cross-held owner's shortest path may run through the edge that closes
 * its cycle, and the visited check alone keeps the search from looping.
 *
 * @param {string} targetId
 * @param {Array<{id, source, target, isSubsidiary?}>} edges - owner -> owned
 * @returns {Map<string, number>}
 */
export const shortestDistances = (targetId, edges) => {
  const ownersOf = new Map();
  const subsidiaries = [];
  edges.forEach((edge) => {
    if (edge.isSubsidiary && edge.source === targetId) {
      subsidiaries.push(edge.target);
    } else {
      pushToMapList(ownersOf, edge.target, edge.source);
    }
  });

  const distances = new Map([[targetId, 0]]);
  const queue = [targetId];
  while (queue.length) {
    const nodeId = queue.shift();
    (ownersOf.get(nodeId) || []).forEach((ownerId) => {
      if (!distances.has(ownerId)) {
        distances.set(ownerId, distances.get(nodeId) + 1);
        queue.push(ownerId);
      }
    });
  }
  subsidiaries.forEach((id) => {
    if (!distances.has(id)) distances.set(id, 1);
  });
  return distances;
};
